import React, { useId } from "react"
import { Box, Typography, useTheme } from "@mui/material"
import DynamicTextareaLabel from "./DynamicTextareaLabel"
import {
    BASE_HEIGHT_PX,
    DEFAULT_MAX_ROWS,
    DEFAULT_ROWS,
    PADDING_HORIZONTAL,
    PADDING_VERTICAL,
    ROW_HEIGHT,
} from "./dynamicTextarea.constants"
import useAutosizingTextarea from "./useAutosizingTextarea"
import {
    characterLimitPropType,
    dynamicTextareaPropTypes,
} from "./dynamicTextarea.propTypes"

const COUNTER_FOOTER_HEIGHT = 32

const CharacterCountTextarea = ({
    label,
    value,
    onChange,
    minRows = DEFAULT_ROWS,
    maxRows = DEFAULT_MAX_ROWS,
    disabled,
    readOnly,
    error,
    helperText,
    characterLimit,
    inputProps,
    onFocus,
    onBlur,
    ...rest
}) => {
    const theme = useTheme()
    const generatedId = useId()
    const labelId = `${generatedId}-label`
    const helperId = `${generatedId}-helper`
    const normalizedMaxRows = Number.isInteger(maxRows) && maxRows >= DEFAULT_ROWS
        ? maxRows
        : DEFAULT_MAX_ROWS
    const minimumHeight = BASE_HEIGHT_PX
    const maximumHeight = Math.max(minimumHeight, normalizedMaxRows * ROW_HEIGHT + PADDING_VERTICAL + COUNTER_FOOTER_HEIGHT)
    const valueAsText = `${value ?? ""}`
    const hasErrorMessage = Boolean(error && helperText)
    const hasPlaceholder = Boolean(inputProps?.placeholder) || Boolean(rest.placeholder)
    const isDisabled = disabled
    const nativeInputProps = { ...(inputProps || {}) }
    const nativeTextareaProps = { ...rest }

    delete nativeInputProps.style
    delete nativeTextareaProps.style

    const tokens = theme.palette.input.dynamicTextarea
    const borderColor = error ? tokens.border.error : tokens.border.default
    const {
        textareaRef,
        height,
        focused,
        showScroll,
        handleChange,
        handleFocus,
        handleBlur,
    } = useAutosizingTextarea({
        value: valueAsText,
        onChange,
        onFocus,
        onBlur,
        minimumHeight,
        maximumHeight,
    })
    const shrink = focused || Boolean(valueAsText) || hasPlaceholder

    return (
        <Box
            sx={{
                width: "100%",
                minWidth: 0,
                position: "relative",
                height,
                border: `1px solid ${borderColor}`,
                backgroundColor: isDisabled ? tokens.background.disabled : tokens.background.default,
                transition: "height 0.2s ease, border-color 0.2s ease, background-color 0.2s ease",
                opacity: isDisabled ? 0.7 : 1,
                "&:hover": isDisabled ? undefined : { backgroundColor: tokens.background.hover },
                "&:focus-within": isDisabled ? undefined : {
                    borderColor: error ? tokens.border.error : tokens.border.focus,
                },
                "& .dynamic-textarea": {
                    display: "block",
                    boxSizing: "border-box",
                    width: "100%",
                    height: "100%",
                    resize: "none",
                    overflowX: "hidden",
                    overflowY: showScroll ? "auto" : "hidden",
                    border: 0,
                    outline: 0,
                    background: "transparent",
                    color: isDisabled ? tokens.text.disabled : tokens.text.default,
                    fontFamily: theme.typography.fontFamily,
                    fontSize: "14px",
                    lineHeight: "20px",
                    padding: `${PADDING_VERTICAL}px ${PADDING_HORIZONTAL}px ${COUNTER_FOOTER_HEIGHT}px`,
                },
            }}
        >
            <DynamicTextareaLabel label={label} labelId={labelId} shrink={shrink} centerWhenResting />
            <textarea
                {...nativeInputProps}
                {...nativeTextareaProps}
                ref={textareaRef}
                rows={minRows}
                value={valueAsText}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                disabled={isDisabled}
                readOnly={readOnly}
                aria-labelledby={labelId}
                aria-describedby={hasErrorMessage ? helperId : undefined}
                aria-invalid={error || undefined}
                className="dynamic-textarea"
                maxLength={characterLimit}
            />
            <Box
                data-dynamic-textarea-footer
                sx={{
                    position: "absolute",
                    left: PADDING_HORIZONTAL,
                    right: PADDING_HORIZONTAL,
                    bottom: 7,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    pointerEvents: "none",
                }}
            >
                <Typography
                    id={hasErrorMessage ? helperId : undefined}
                    component="span"
                    variant="caption"
                    sx={{ color: theme.palette.text.warning, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                    {hasErrorMessage ? helperText : ""}
                </Typography>
                <Typography
                    component="span"
                    variant="caption"
                    aria-label={`${valueAsText.length} de ${characterLimit} caracteres`}
                    sx={{
                        color: error ? theme.palette.common.white : theme.palette.icon.white70,
                        backgroundColor: error ? theme.palette.text.warning : "transparent",
                        borderRadius: "2px",
                        fontWeight: error ? 600 : 400,
                        marginLeft: "auto",
                        padding: error ? "1px 4px" : 0,
                        whiteSpace: "nowrap",
                    }}
                >
                    {valueAsText.length}/{characterLimit}
                </Typography>
            </Box>
        </Box>
    )
}

CharacterCountTextarea.propTypes = {
    ...dynamicTextareaPropTypes,
    characterLimit: characterLimitPropType,
}

export default CharacterCountTextarea
